import {AxiosInstance} from 'axios'
import MyAxiosInstanceLevantamento from './axiosInstanceLevantamento'
import {
    AlternativaCreate,
    AlternativaOut,
    AlternativaPatch,
    ConcluirOut,
    Diferencial,
    EscolherPayload,
    EscolherSolucaoPayload,
    Hipotese74,
    LevantamentoCreate,
    LevantamentoDetail,
    LevantamentoOut,
    LevantamentoResumo,
    Requisito,
    SelecionarSolucoesPayload,
} from '../types/levantamento'

// Service do módulo Levantamento de Mercado. Consome o levantamento-service —
// a baseURL (VITE_LEVANTAMENTO_URL) já contém o prefixo /api/levantamento/v1
// (local: http://localhost:8084/api/levantamento/v1).
//
// Endpoints (contrato F1):
//   POST   /levantamentos                           -> 202 {id, status: CONVERSANDO}
//   GET    /levantamentos                           -> LevantamentoResumo[]
//   GET    /levantamentos/{id}                      -> LevantamentoDetail (alvo do polling)
//   POST   /levantamentos/{id}/mensagens            -> 202 (resposta da IA via mensagens)
//   POST   /levantamentos/{id}/alternativas         -> 200 AlternativaOut
//   PATCH  /levantamentos/{id}/alternativas/{aid}   -> 200 AlternativaOut
//   DELETE /levantamentos/{id}/alternativas/{aid}
//   POST   /levantamentos/{id}/investigar           -> 202 (INVESTIGANDO -> COMPARANDO)
//   POST   /levantamentos/{id}/escolher             -> 202 (IA redige justificativa_final)
//   PATCH  /levantamentos/{id}/justificativa        -> 200
//   POST   /levantamentos/{id}/concluir             -> 200 {resultado}
//   GET    /levantamentos/{id}/relatorio.html       -> HTML imprimível
//   GET    /levantamentos/{id}/export?formato=docx  -> bytes
export class LevantamentoApiService {
    private api: AxiosInstance

    constructor() {
        this.api = MyAxiosInstanceLevantamento
    }

    // Cria o levantamento (202) — dispara as perguntas de contexto da
    // Licito.guru IA em background; acompanhar via getLevantamento.
    async createLevantamento(
        payload: LevantamentoCreate
    ): Promise<LevantamentoOut> {
        const {data} = await this.api.post('/levantamentos', payload)
        return data
    }

    async listLevantamentos(): Promise<LevantamentoResumo[]> {
        const {data} = await this.api.get('/levantamentos')
        return data
    }

    async getLevantamento(id: string): Promise<LevantamentoDetail> {
        const {data} = await this.api.get(`/levantamentos/${id}`)
        return data
    }

    // Turno do usuário na conversa (202) — a resposta da IA entra em
    // `mensagens`; o status pode virar ALTERNATIVAS_PRONTAS quando a IA
    // propõe o mapa de alternativas.
    async enviarMensagem(id: string, conteudo: string): Promise<void> {
        await this.api.post(`/levantamentos/${id}/mensagens`, {conteudo})
    }

    // Responde UMA pergunta de contexto estruturada (sugestão clicada ou
    // resposta digitada). A última resposta dispara o mapa de alternativas.
    async responderPergunta(
        id: string,
        indice: number,
        resposta: string
    ): Promise<void> {
        await this.api.post(`/levantamentos/${id}/responder`, {indice, resposta})
    }

    async addAlternativa(
        id: string,
        payload: AlternativaCreate
    ): Promise<AlternativaOut> {
        const {data} = await this.api.post(
            `/levantamentos/${id}/alternativas`,
            payload
        )
        return data
    }

    async editAlternativa(
        id: string,
        alternativaId: string,
        payload: AlternativaPatch
    ): Promise<AlternativaOut> {
        const {data} = await this.api.patch(
            `/levantamentos/${id}/alternativas/${alternativaId}`,
            payload
        )
        return data
    }

    async deleteAlternativa(id: string, alternativaId: string): Promise<void> {
        await this.api.delete(
            `/levantamentos/${id}/alternativas/${alternativaId}`
        )
    }

    // Dispara a investigação de mercado das alternativas (202) — coleta
    // PNCP + varejo por alternativa em background (INVESTIGANDO -> COMPARANDO).
    async investigar(id: string): Promise<void> {
        await this.api.post(`/levantamentos/${id}/investigar`)
    }

    // Marca a solução escolhida (202) — a Licito.guru IA redige a
    // justificativa técnica e econômica em `justificativa_final`.
    async escolher(id: string, payload: EscolherPayload): Promise<void> {
        await this.api.post(`/levantamentos/${id}/escolher`, payload)
    }

    // ------------------- fluxo novo (usuário no comando) --------------------

    // Escolhe o CONCEITO de contratação (202) — a investigação pesada roda SÓ
    // para ele; soluções e referências chegam por push.
    async escolherConceito(id: string, alternativaId: string): Promise<void> {
        await this.api.post(`/levantamentos/${id}/escolher-conceito`, {
            alternativa_id: alternativaId,
        })
    }

    // Marca as soluções que entram na comparação (mín. 1; manuais permitidas).
    // Com 2+ marcadas, o parecer comparativo chega por push (PARECER_PRONTO).
    async selecionarSolucoes(
        id: string,
        payload: SelecionarSolucoesPayload
    ): Promise<void> {
        await this.api.post(`/levantamentos/${id}/solucoes/selecionar`, payload)
    }

    // Escolha final: solução específica (dispara os DIFERENCIAIS da IA — push
    // diferenciais_prontos) OU sem_preferencia=true (dispara os REQUISITOS
    // essenciais — push requisitos_prontos). A justificativa roda após a
    // confirmação supervisionada do gestor.
    async escolherSolucao(
        id: string,
        payload: EscolherSolucaoPayload
    ): Promise<void> {
        await this.api.post(`/levantamentos/${id}/escolher-solucao`, payload)
    }

    // Confirma os diferenciais (mín. 1) — SÓ os confirmados fundamentam a
    // justificativa (push justificativa_pronta).
    async confirmarDiferenciais(
        id: string,
        confirmados: Diferencial[]
    ): Promise<void> {
        await this.api.post(`/levantamentos/${id}/diferenciais`, {confirmados})
    }

    // Informa/corrige o SITE OFICIAL de uma solução da comparação (202): o
    // backend grava, relê o site em background e — quando a solução é a
    // preferida — refaz a análise de diferenciais com o material novo (push
    // diferenciais_prontos). Usado no diagnóstico "não foi possível ler o
    // site de X" do passo de diferenciais.
    async corrigirSiteSolucao(
        id: string,
        payload: {nome_solucao: string; site: string}
    ): Promise<void> {
        await this.api.post(`/levantamentos/${id}/solucoes/site`, payload)
    }

    // Atalho do passo Soluções: define requisitos técnicos essenciais e segue
    // para o caminho competitivo (push requisitos_prontos).
    // Declara o caminho da contratação (art. 74). Síncrono: é ato do processo, não
    // processamento — devolve o levantamento já com a declaração gravada.
    async declararHipotese(
        levantamentoId: string,
        hipotese: Hipotese74 | null,
        alinea?: string | null,
        observacoes?: string
    ): Promise<LevantamentoDetail> {
        const {data} = await this.api.post(
            `/levantamentos/${levantamentoId}/hipotese`,
            {hipotese, alinea: alinea ?? null, observacoes: observacoes ?? ''}
        )
        return data
    }

    // Investiga o profissional e monta o dossiê do art. 74, §3º. Só se aplica quando o
    // caminho declarado é o do profissional determinado (II ou III).
    async montarDossie(levantamentoId: string): Promise<LevantamentoDetail> {
        const {data} = await this.api.post(`/levantamentos/${levantamentoId}/dossie`)
        return data
    }

    async iniciarRequisitosEssenciais(
        id: string,
        observacoes?: string
    ): Promise<void> {
        await this.api.post(`/levantamentos/${id}/requisitos-essenciais`, {
            observacoes,
        })
    }

    // Confirma os requisitos (mín. 1) e dispara a justificativa competitiva.
    async confirmarRequisitos(
        id: string,
        confirmados: Requisito[]
    ): Promise<void> {
        await this.api.post(`/levantamentos/${id}/requisitos-essenciais/confirmar`, {
            confirmados,
        })
    }

    // Pedido de informações ao mercado (RFI): o sistema NUNCA envia nada — o
    // envio é do órgão pelos canais oficiais (push rfi_pronto).
    async elaborarRfi(
        id: string,
        destinatario?: {nome: string; fornecedor?: string; site?: string}
    ): Promise<{rfi_id: string}> {
        const {data} = await this.api.post(`/levantamentos/${id}/rfi`, {
            destinatario,
        })
        return data
    }

    async editarRfi(id: string, rfiId: string, texto: string): Promise<void> {
        await this.api.patch(`/levantamentos/${id}/rfi/${rfiId}`, {texto})
    }

    async exportarRfi(
        id: string,
        rfiId: string,
        formato: 'docx' | 'pdf'
    ): Promise<void> {
        const {data} = await this.api.get(
            `/levantamentos/${id}/rfi/${rfiId}/export`,
            {params: {formato}, responseType: 'blob'}
        )
        this.baixarBlob(data, `pedido-informacoes.${formato}`)
    }

    // Anotação leve de rastreabilidade (vira mensagem de sistema; nunca IA).
    async registrarAnotacao(id: string, texto: string): Promise<void> {
        await this.api.post(`/levantamentos/${id}/anotacoes`, {texto})
    }

    // Retry explícito da pesquisa de soluções quando a fonte de busca falhou
    // (busca_status FALHOU): mesmo pipeline, resultado por push (202).
    async repesquisarSolucoes(id: string): Promise<void> {
        await this.api.post(`/levantamentos/${id}/repesquisar-solucoes`)
    }

    async salvarJustificativa(id: string, texto: string): Promise<void> {
        await this.api.patch(`/levantamentos/${id}/justificativa`, {texto})
    }

    // Handoff manual → Pesquisa de Preços: vincula a pesquisa criada lá à
    // ALTERNATIVA de origem (idempotente: regravar substitui). O usuário pode
    // pesquisar preços de qualquer alternativa ainda durante a confecção.
    async vincularPesquisa(
        id: string,
        alternativaId: string,
        pesquisaId: string
    ): Promise<void> {
        await this.api.post(
            `/levantamentos/${id}/alternativas/${alternativaId}/vincular-pesquisa`,
            {pesquisa_id: pesquisaId}
        )
    }

    // Consolida o relatório final (status CONCLUIDO).
    async concluir(id: string): Promise<ConcluirOut> {
        const {data} = await this.api.post(`/levantamentos/${id}/concluir`)
        return data
    }

    // Relatório HTML imprimível — texto para renderizar em iframe (srcDoc).
    async getRelatorioHtml(id: string): Promise<string> {
        const {data} = await this.api.get(
            `/levantamentos/${id}/relatorio.html`,
            {responseType: 'text', transformResponse: [(v) => v]}
        )
        return data
    }

    // Downloads do relatório: SÓ DOCX ou PDF (decisão do dono — o download de
    // HTML foi removido; o relatorio.html segue interno p/ a tela).
    private baixarBlob(data: BlobPart, nome: string): void {
        const url = window.URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', nome)
        document.body.appendChild(link)
        link.click()
        link.parentNode?.removeChild(link)
        window.URL.revokeObjectURL(url)
    }

    // Baixa o relatório em DOCX.
    async exportarDocx(id: string): Promise<void> {
        const {data} = await this.api.get(`/levantamentos/${id}/export`, {
            params: {formato: 'docx'},
            responseType: 'blob',
        })
        this.baixarBlob(data, 'levantamento-mercado.docx')
    }

    // Baixa o relatório em PDF (weasyprint no backend — padrão da casa).
    async exportarPdf(id: string): Promise<void> {
        const {data} = await this.api.get(`/levantamentos/${id}/export`, {
            params: {formato: 'pdf'},
            responseType: 'blob',
        })
        this.baixarBlob(data, 'levantamento-mercado.pdf')
    }
}
